import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import { adsService } from '@/services/AdsService'
import { adsKeys } from './queryKeys'
import type { CompanyAdsDto, VenueAdsDto, VenueAdsPeriodDto } from '@lib/dto/ad-numbers.dto'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import type {
  AdAccountDto,
  AdCampaignDto,
  AdRefreshDto,
  AdRemovalPreviewDto,
  AdSignInDoneDto,
  AdSignInStartDto,
  AdsSetupDto,
} from '@lib/dto/ads.dto'
import type { LeadStatsView } from '@lib/enums/lead-stats-view.enum'
import type {
  FinishAdSignInInput,
  RefreshAdsInput,
  StartAdSignInInput,
  UpdateAdAccountInput,
  UpdateAdCampaignInput,
} from '@lib/input/ads.input'

/**
 * The company's ads set-up. `enabled` is false for a company without the Ads
 * feature, which the API would refuse.
 */
export function useAdsSetup(enabled: boolean): UseQueryResult<AdsSetupDto> {
  return useQuery({
    queryKey: adsKeys.setup(),
    queryFn: () => adsService.setup(),
    enabled,
    // A failure here is the feature being off or Google set-up missing; the page
    // says which. Retrying only delays telling the person.
    retry: false,
    // While anything is importing, look again every few seconds so the page
    // moves on by itself when it finishes.
    refetchInterval: (query) => (query.state.data?.accounts.some((a) => a.importing) === true ? 5_000 : false),
  })
}

/** Ask where to send the person to sign in with Google; the caller goes there. */
export function useStartAdSignIn(): UseMutationResult<AdSignInStartDto, Error, StartAdSignInInput> {
  return useMutation({ mutationFn: (input: StartAdSignInInput) => adsService.startSignIn(input) })
}

/** Keep what Google's sign-in screen sent back. */
export function useFinishAdSignIn(): UseMutationResult<AdSignInDoneDto, Error, FinishAdSignInInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: FinishAdSignInInput) => adsService.finishSignIn(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adsKeys.all }),
  })
}

export function useRemoveAdConnection(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (connectionId: string) => adsService.removeConnection(connectionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adsKeys.all }),
  })
}

/** Put an account in use, point it elsewhere, ignore it, or stop or start it. */
export function useUpdateAdAccount(): UseMutationResult<AdAccountDto, Error, { accountId: string; input: UpdateAdAccountInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ accountId, input }: { accountId: string; input: UpdateAdAccountInput }) =>
      adsService.updateAccount(accountId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adsKeys.all }),
  })
}

/** What removing an account from its venue would delete. Fetched only while the confirmation is open. */
export function useAdRemovalPreview(accountId: string | null): UseQueryResult<AdRemovalPreviewDto> {
  return useQuery({
    queryKey: adsKeys.removal(accountId ?? 'none'),
    queryFn: () => adsService.removalPreview(accountId ?? ''),
    enabled: accountId !== null,
    retry: false,
    // Always fresh: it is read once, right before someone confirms deleting it.
    staleTime: 0,
  })
}

export function useRemoveAdAccountFromVenue(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (accountId: string) => adsService.removeFromVenue(accountId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adsKeys.all }),
  })
}

/** Give a campaign a place of its own, or let it follow its account again. */
export function useUpdateAdCampaign(): UseMutationResult<AdCampaignDto, Error, { campaignId: string; input: UpdateAdCampaignInput }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ campaignId, input }: { campaignId: string; input: UpdateAdCampaignInput }) =>
      adsService.updateCampaign(campaignId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adsKeys.all }),
  })
}

/** Read Google again now: one venue's accounts, or all of the company's. */
export function useRefreshAds(): UseMutationResult<AdRefreshDto, Error, RefreshAdsInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RefreshAdsInput) => adsService.refresh(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adsKeys.all }),
  })
}

/** While Google is being read, look again every few seconds so the numbers move on by themselves. */
const WHILE_IMPORTING = 5_000

/** The company's table for one period (the newest when null). */
export function useCompanyAds(period: CalendarDay | null, enabled: boolean): UseQueryResult<CompanyAdsDto> {
  return useQuery({
    queryKey: adsKeys.company(period),
    queryFn: () => adsService.company(period),
    enabled,
    retry: false,
    refetchInterval: (query) => (query.state.data?.importing === true ? WHILE_IMPORTING : false),
  })
}

/** A venue's periods, tracks and accounts. */
export function useVenueAds(venueId: string, enabled: boolean): UseQueryResult<VenueAdsDto> {
  return useQuery({
    queryKey: adsKeys.venue(venueId),
    queryFn: () => adsService.venue(venueId),
    enabled: enabled && venueId !== '',
    retry: false,
    refetchInterval: (query) => (query.state.data?.importing === true ? WHILE_IMPORTING : false),
  })
}

/** One period at a venue, campaign by campaign. Fetched when the period is opened. */
export function useVenueAdsPeriod(venueId: string, start: CalendarDay | null, view: LeadStatsView = 'cohort'): UseQueryResult<VenueAdsPeriodDto> {
  return useQuery({
    queryKey: adsKeys.venuePeriod(venueId, start ?? 'none', view),
    queryFn: () => (start === null ? Promise.reject(new Error('No period chosen')) : adsService.venuePeriod(venueId, start, view)),
    enabled: start !== null,
    retry: false,
  })
}
